<template>
  <div v-if="showPlayer" class="ambient-player">
    <div class="player-container" :class="{ 'minimized': isMinimized }">
      <!-- Minimized state -->
      <div v-if="isMinimized" class="minimized-player" @click="isMinimized = false">
        <div class="mini-album-art">☯️</div>
        <div class="mini-info">
          <div class="mini-track">{{ currentTrack.title }}</div>
          <div class="mini-artist">{{ currentTrack.artist }}</div>
        </div>
        <button class="mini-play-pause" @click.stop="togglePlayPause">
          {{ isPlaying ? '⏸' : '▶' }}
        </button>
      </div>

      <!-- Expanded state -->
      <div v-else class="expanded-player">
        <div class="album-art">
          <div class="art-placeholder">☯️</div>
        </div>
        
        <div class="player-info">
          <h3 class="album-type">Ambient</h3>
          <h2 class="track-title">{{ currentTrack.title }}</h2>
          <div class="track-meta">
            <img v-if="currentTrack.avatar" :src="currentTrack.avatar" class="artist-avatar" alt="" />
            <span class="artist-name">{{ currentTrack.artist }}</span>
            <span>·</span>
            <span class="album-meta">{{ currentTrack.year }}</span>
            <span>·</span>
            <span class="album-meta">{{ currentTrack.tracks }} tracks</span>
            <span>·</span>
            <span class="album-meta">{{ currentTrack.duration }}</span>
          </div>
        </div>

        <div class="player-controls">
          <button class="control-btn shuffle" :class="{ active: isShuffling }" @click="toggleShuffle">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M16 3h5v5M4 20L21 3M21 3v5h-5M21 21l-6-6M16 21h5v-5"></path>
            </svg>
          </button>
          
          <button class="control-btn add" @click="handleAdd">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 5v14M5 12h14"></path>
            </svg>
          </button>

          <button class="control-btn download" @click="handleDownload">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"></path>
            </svg>
          </button>

          <button class="control-btn more" @click="handleMore">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="12" cy="5" r="1"></circle>
              <circle cx="12" cy="19" r="1"></circle>
            </svg>
          </button>

          <button class="control-btn list" @click="showTrackList">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="main-controls">
          <div class="album-art-mini">
            <div class="art-placeholder-mini">☯️</div>
          </div>
          
          <div class="play-pause-buttons">
            <button class="play-btn" @click="togglePlayPause">
              {{ isPlaying ? '⏸' : '▶' }}
            </button>
          </div>
        </div>

        <button class="minimize-btn" @click="isMinimized = true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

interface Track {
  title: string
  artist: string
  year: string
  tracks: string
  duration: string
  avatar?: string
}

const showPlayer = ref(false)
const isMinimized = ref(true)
const isPlaying = ref(false)
const isShuffling = ref(false)
const currentTrack = ref<Track>({
  title: 'A Raver\'s Diary - The Extended Versions',
  artist: 'DUSTY KID',
  year: '2009',
  tracks: '11 songs',
  duration: '1 hr 30 min'
})

const togglePlayPause = () => {
  isPlaying.value = !isPlaying.value
  // In a real implementation, this would control actual audio playback
  console.log(isPlaying.value ? 'Playing' : 'Paused')
}

const toggleShuffle = () => {
  isShuffling.value = !isShuffling.value
}

const handleAdd = () => {
  console.log('Add to library')
}

const handleDownload = () => {
  console.log('Download')
}

const handleMore = () => {
  console.log('More options')
}

const showTrackList = () => {
  console.log('Show track list')
}

// Show player on specific routes (meditation pages)
onMounted(() => {
  const allowedRoutes: string[] = []
  const routeName = route.name?.toString()
  
  if (routeName && allowedRoutes.some(r => routeName.includes(r))) {
    showPlayer.value = true
  }
})
</script>

<style scoped>
.ambient-player {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
  max-width: 420px;
}

.player-container {
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  transition: all 0.3s ease;
}

/* Minimized state */
.minimized-player {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.minimized-player:hover {
  background: rgba(255, 255, 255, 0.05);
}

.mini-album-art {
  font-size: 2rem;
  flex-shrink: 0;
}

.mini-info {
  flex: 1;
  min-width: 0;
}

.mini-track {
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.15rem;
}

.mini-artist {
  color: #9ca3af;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mini-play-pause {
  background: #22c55e;
  color: white;
  border: none;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.mini-play-pause:hover {
  background: #16a34a;
  transform: scale(1.05);
}

/* Expanded state */
.expanded-player {
  padding: 1.5rem;
}

.album-art {
  width: 100%;
  aspect-ratio: 1;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
  border-radius: 12px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.art-placeholder {
  font-size: 8rem;
  opacity: 0.8;
}

.album-type {
  color: #9ca3af;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  margin: 0 0 0.5rem 0;
}

.track-title {
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
  line-height: 1.2;
}

.track-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9ca3af;
  font-size: 0.85rem;
  margin-bottom: 1.5rem;
}

.artist-avatar {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  object-fit: cover;
}

.artist-name {
  color: #ffffff;
  font-weight: 500;
}

.album-meta {
  color: #9ca3af;
}

.player-controls {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.control-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.2);
}

.control-btn.active {
  color: #22c55e;
  border-color: #22c55e;
}

.main-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.album-art-mini {
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.art-placeholder-mini {
  font-size: 1.5rem;
  opacity: 0.8;
}

.play-btn {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: #22c55e;
  color: white;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  flex-shrink: 0;
}

.play-btn:hover {
  background: #16a34a;
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(34, 197, 94, 0.4);
}

.minimize-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border-radius: 6px;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.minimize-btn:hover {
  background: rgba(0, 0, 0, 0.5);
  border-color: rgba(255, 255, 255, 0.2);
}

@media (max-width: 768px) {
  .ambient-player {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }

  .track-title {
    font-size: 1.25rem;
  }

  .album-art {
    margin-bottom: 0.75rem;
  }

  .art-placeholder {
    font-size: 5rem;
  }
}

@media (max-width: 480px) {
  .expanded-player {
    padding: 1rem;
  }
}
</style>

